import React, { useContext, useRef, useEffect } from 'react';
import { getContext, useCombinedRefs } from 'common';
import { CONSTANTS } from 'common/ListKeyboardNav';
import { ListItemText, ListContext } from 'components/List';
import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
} = CONSTANTS;

export interface SelectItemCreatedItemProps extends React.ComponentPropsWithRef<'div'> {
  /** The string to display before the new value to create.  */
  createLabel?: React.ReactNode;
  /** The value that will be created. */
  createValue?: string;
  /** Used to focus this item in the dropdown list when this item is the item that is scrolled to or selected. Useful during keyboard
   * navigation of a virtualized list. */
  focusItem?: boolean;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
}

/** Item to display in a SelectItem dropdown menu as the item to 'Create'.
 * Any other props will be provided to the root element.
 */
const SelectItemCreatedItem = React.forwardRef<HTMLElement, SelectItemCreatedItemProps>((props: SelectItemCreatedItemProps, forwardedRef) =>
  getContext(({ RTL, theme }) => {
    const {
      createLabel,
      createValue,
      focusItem,
      ...otherProps
    } = props;
    const listContext = useContext(ListContext);
    const innerRef = useRef(null);
    const ref = useCombinedRefs(forwardedRef, innerRef);

    useEffect(() => {
      if (focusItem === true) {
        innerRef && innerRef.current && innerRef.current.focus();
      }
    }, [focusItem]);

    const handleClick = (event) => {
      const { onClick } = props;
      onClick && onClick(event);
    };

    const handleKeyDown = (event) => {
      const { onKeyDown } = props;
      onKeyDown && onKeyDown(event);
    };

    const listTabableName = listContext.uniqueListId ?
      `${LIST_ITEM_TABABLE_ITEM}-${listContext.uniqueListId}` : LIST_ITEM_TABABLE_ITEM;

    const resolvedTheme = theme && theme.LIST && theme.LIST.VERTICAL;

    return (
      <Styled.SelectListItem
        className={listTabableName}
        ref={ref}
        RTL={RTL}
        $theme={resolvedTheme}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        hoverEffect
        {...otherProps}
      >
        <ListItemText ref={ref}>
          {createLabel} &ldquo;{createValue}&rdquo;
        </ListItemText>
      </Styled.SelectListItem>
    );
  }));

SelectItemCreatedItem.displayName = 'SelectItemCreatedItem';

SelectItemCreatedItem.defaultProps = {
  createLabel: 'Add',
  createValue: undefined,
  focusItem: false,
  onClick: undefined,
  onKeyDown: undefined
};

export { SelectItemCreatedItem as default };
